import React from 'react'
import { AppContext } from 'contexts/AppContext'
import { APIHelper, API } from 'api/APIClient'
import Axios from "axios";
import {ContextUtil} from "../utils/ContextUtil";

const localStorageSessionKey = "uw.localstorage.sessiondata";
const TWO_FACTOR_MAX_AGE_SECONDS = 30 * 60;

export default class AppProvider extends React.Component {

    state = {
        user: null,
        organisation: null,
        permissions: null,
        authToken: null,
        menu: [],
        twoFactorPending: false,
        twoFactorData: null
    }

    getCleanTwoFactorData = (twoFactorData) => {
        if (!twoFactorData || !twoFactorData.email || !twoFactorData.expiresAt) {
            return null;
        }

        const now = Date.now();
        const maxAgeMs = TWO_FACTOR_MAX_AGE_SECONDS * 1000;
        if (twoFactorData.expiresAt <= now || (twoFactorData.createdAt && (now - twoFactorData.createdAt) > maxAgeMs)) {
            return null;
        }

        return {
            email: twoFactorData.email,
            expiresAt: twoFactorData.expiresAt,
            resendAvailableAt: twoFactorData.resendAvailableAt || now,
            createdAt: twoFactorData.createdAt || now
        };
    }

    componentDidMount() {
        let session = sessionStorage.getItem(localStorageSessionKey);
        if (session) {
            session = JSON.parse(session);
            const twoFactorData = this.getCleanTwoFactorData(session.twoFactorData);
            this.setState({
                ...session,
                twoFactorPending: !!twoFactorData,
                twoFactorData: twoFactorData
            });

            // CADE 2024/04/03 - Init Context Util here as well.
            if (session.authToken) {
                ContextUtil.init(session);
            }
        }
    }

    componentDidUpdate() {
        if (this.state.twoFactorPending && this.state.twoFactorData && this.state.twoFactorData.expiresAt <= Date.now()) {
            this.clearPendingTwoFactor();
            return;
        }

        sessionStorage.setItem(localStorageSessionKey, JSON.stringify(this.state));
        if (this.state.authToken) {
            APIHelper.setAuthToken(this.state.authToken);
            APIHelper.setOrganisationID(this.state.organisation.id);
            APIHelper.onAuthFailed(this.logout)
        }
        else {
            APIHelper.removeAuthToken();
            APIHelper.removeOrganisationID();
        }
    }

    login = (data) => {
        if (data.user && data.organisation && data.permissions && data.authToken) {
            // CADE 2024-04-03 - Mirror Context data in a static state so we can
            // read from within functionised components.
            ContextUtil.init(data);

            this.setState({
                user: data.user,
                organisation: data.organisation,
                permissions: data.permissions,
                authToken: data.authToken,
                menu: data.menu,
                twoFactorPending: false,
                twoFactorData: null
            });
        }
    }

    setPendingTwoFactor = (data) => {
        const now = Date.now();
        const expiresInSeconds = Math.max(0, parseInt(data.expiresInSeconds || 0, 10));
        const resendAvailableInSeconds = Math.max(0, parseInt(data.resendAvailableInSeconds || 0, 10));
        const twoFactorData = {
            email: data.email,
            expiresAt: now + (expiresInSeconds * 1000),
            resendAvailableAt: now + (resendAvailableInSeconds * 1000),
            createdAt: now
        };

        this.setState({
            user: null,
            organisation: null,
            permissions: null,
            authToken: null,
            menu: [],
            twoFactorPending: true,
            twoFactorData: twoFactorData
        });
    }

    clearPendingTwoFactor = () => {
        this.setState({
            twoFactorPending: false,
            twoFactorData: null
        });
    }

    logout = () => {
        const clearLocalAuthState = () => {
            this.setState({
                user: null,
                organisation: null,
                permissions: null,
                authToken: null,
                menu: [],
                twoFactorPending: false,
                twoFactorData: null
            }, () => {
                APIHelper.removeAuthToken();
                APIHelper.removeOrganisationID();
            });
        };

        if (!this.state.authToken) {
            clearLocalAuthState();
            return;
        }

        Axios
            .post(API.auth.logout)
            .then(() => clearLocalAuthState())
            .catch(() => clearLocalAuthState())
    }

    // apiClient = (function(that){
    //     return axios.create({
    //         baseURL: "http://10.128.0.152:8999/cmsapi/",
    //         headers: {
    //             "AuthToken": that.state.authToken || ""
    //         },
    //         validateStatus: (status) => {
    //             if (status === 401 || status === 403) {
    //                 that.logout();
    //             }
    //             else {
    //                 return status;
    //             }
    //         }
    //     });
    // })(this);

    render() {
        return (
            <AppContext.Provider
                value={{
                    user: this.state.user,
                    organisation: this.state.organisation,
                    permissions: this.state.permissions,
                    menu: this.state.menu,
                    twoFactorPending: this.state.twoFactorPending,
                    twoFactorData: this.state.twoFactorData,
                    login: this.login,
                    logout: this.logout,
                    setPendingTwoFactor: this.setPendingTwoFactor,
                    clearPendingTwoFactor: this.clearPendingTwoFactor
                }}
            >
                {this.props.children}
            </AppContext.Provider>
        )
    }

}
